import * as React from "react"
import { FC, useEffect, useState } from "react"
import { ProfileProps, User } from "../../../../types"
import { saveUserData, saveUserProfilePhoto } from "../../../../actions"
import { connect } from "react-redux"
import { RootState } from "../../../../store"
import { KButton, KInput, KSpan, KTitleSpan } from "kahuna-base-react-components"
import UploadImage from "./UploadImage"
import { toast } from "react-toastify"
import ChangePasswordButton from "../security/ChangePasswordButton"
import { errorFilledToastOptions, infoFilledToastOptions } from "../../../../constants"
import { lang } from "../../../../constants/languages"
import { users } from "../../../../apis"

const Profile: FC<ProfileProps> = (props) => {
  const [initialUser, setInitialUser] = useState<User | null>(props.user)
  const [user, setUser] = useState<User | null>(props.user)
  const [loadingPhoto, setLoadingPhoto] = useState(false)
  const [qr, setQr] = useState()
  const [code, setCode] = useState("")

  const onFormSubmit = async (values?: User) => {
    if (!values) {
      return
    }
    if (values.firstName === "") {
      toast.error(lang.account.profile.error.first_name_blank, errorFilledToastOptions)
      setUser((prevUser: any) => {
        return { ...prevUser, firstName: props.user.firstName }
      })
    } else if (values.lastName === "") {
      toast.error(lang.account.profile.error.last_name_blank, errorFilledToastOptions)
      setUser((prevUser: any) => {
        return { ...prevUser, lastName: props.user.lastName }
      })
    } else {
      if (
        values.firstName !== initialUser?.firstName ||
        values.lastName !== initialUser?.lastName ||
        values.bio !== initialUser?.bio ||
        values.phone !== initialUser?.phone
      ) {
        setInitialUser((prevUser: any) => {
          return {
            ...prevUser,
            firstName: values.firstName,
            lastName: values.lastName,
            bio: values.bio,
            phone: values.phone
          }
        })

        props.saveUserData(values)
      }
    }
  }

  const handleValueChange = (newValue: string, key: string) => {
    setUser((prevUser: any) => {
      return { ...prevUser, [key]: newValue }
    })
  }

  useEffect(() => {
    setUser(props.user)
  }, [props.user])

  const onUploadImage = (photo: FormData) => {
    setLoadingPhoto(true)
    toast.loading(lang.account.profile.loading_photo, { toastId: "LOADING_PHOTO", ...infoFilledToastOptions })
    props.saveUserProfilePhoto(photo).then(() => {
      setLoadingPhoto(false)
      toast.dismiss("LOADING_PHOTO")
    })
  }

  const getTotpQrCode = () => {
    users(`/totp_qr/`, "get")
      .then((resp) => {
        if (resp.data.qr) {
          setQr(resp.data.qr)
        } else {
          toast.error("Error!")
        }
      })
      .catch(() => toast.error("Error!"))
  }

  const authenticateTotp = () => {
    users(`/authenticate_totp/?code=${code}`, "put")
      .then((resp) => {
        console.log(resp.data)
        if (resp.data.success) {
          toast.success("Success!")
          setQr(undefined)
          //@ts-ignore
          setUser({ ...user, totpEnabled: true })
        } else {
          toast.error("Error!")
        }
      })
      .catch(() => toast.error("Error!"))
  }

  return (
    <>
      <div className="flex flex-col gap-6 ">
        <div>
          <UploadImage
            loading={loadingPhoto}
            sectionTitle={lang.account.profile.upload_image_title}
            sectionDetail={lang.account.profile.upload_image_desc}
            onClick={onUploadImage}
            buttonTitle={lang.account.profile.upload_image_button}
            iconPath={user?.photo || "/account_icons/avatar.svg"}
          />
        </div>
        <div className="w-full flex flex-row gap-[16px]">
          <div className="w-[calc(50%)]">
            <KInput
              value={user!.firstName}
              placeholder={lang.account.profile.first_name_placeholder}
              onChange={(text) => handleValueChange(text, "firstName")}
              onBlur={() => {
                onFormSubmit(user!)
              }}
              padding="14px"
              border="1px solid #F3F3F3"
              boxShadow=" 0px 1px 2px 0px rgba(228, 229, 231, 0.24)"
            />
          </div>
          <div className="w-[calc(50%)]">
            <KInput
              value={user!.lastName}
              placeholder={lang.account.profile.last_name_placeholder}
              onChange={(text) => handleValueChange(text, "lastName")}
              onBlur={() => {
                onFormSubmit(user!)
              }}
              padding="14px"
              border="1px solid #F3F3F3"
              boxShadow=" 0px 1px 2px 0px rgba(228, 229, 231, 0.24)"
            />
          </div>
        </div>
        <div className="border-dashed border-b-[1px]" />
        <div className="flex justify-between" style={{ paddingRight: 1 }}>
          <div
            className="w-[calc(49%)] flex items-center"
            style={{
              borderRadius: 10,
              background: "var(--bg-weak-100, #F0F0F0)",
              padding: 14
            }}
          >
            <KSpan text={user?.email || " - "} />
          </div>
          <ChangePasswordButton />
        </div>
        <div className="border-dashed border-b-[1px]" />
        <div>
          <div>
            <KSpan text="2FA" fontSize={16} color="#111" fontWeight={500} />
          </div>
          <div className="mt-2 flex gap-2">
            <div className="w-[calc(50%)]">
              <KInput
                placeholder={lang.account.profile.phone_placeholder}
                onChange={(text) => handleValueChange(text, "phone")}
                value={user?.phone || ""}
                padding="14px"
                border="1px solid #F3F3F3"
                boxShadow=" 0px 1px 2px 0px rgba(228, 229, 231, 0.24)"
                onBlur={() => onFormSubmit(user || undefined)}
              />
            </div>
            <div className="w-[calc(50%)] flex justify-end items-center">
              {user?.totpEnabled ? (
                <KSpan text={lang.account.profile["2fa_enabled_status"]} />
              ) : (
                <KButton
                  width="150px"
                  height="50px"
                  background="black"
                  disabled={!user?.phone}
                  textColor="white"
                  text={lang.account.profile.enable_2fa}
                  onClick={() => getTotpQrCode()}
                />
              )}
            </div>
          </div>
          {qr && (
            <div className="mt-4">
              <KSpan text={lang.account.profile.qr_title} />
              <div className="flex gap-4">
                <img src={`data:image/png;base64,${qr}`} alt="TOTP QR" style={{ width: 160, height: 160 }} />
                <div className="mt-3">
                  <div className="mb-3">
                    <KInput
                      value={code}
                      onChange={(text) => setCode(text)}
                      placeholder={lang.account.profile.enter_code}
                    />
                  </div>
                  <KButton
                    text={lang.account.profile.authenticate}
                    background="black"
                    textColor="white"
                    onClick={() => authenticateTotp()}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const mapStateToProps = (state: RootState) => {
  return { user: state.auth.user }
}

export default connect(mapStateToProps, { saveUserData, saveUserProfilePhoto })(Profile)
